import React, { useState } from 'react';
import { Sparkles, Upload, Wand2, ShoppingBag, Zap, CheckCircle2, Star, TrendingUp, Heart } from 'lucide-react';
import Button from 'shared/components/ui/Button';
import { Card } from 'shared/components/ui/Card';
import { useTranslation } from 'react-i18next';
import { URLS } from 'configs';
import inspirationImg from 'assets/landing/lookfinder/inspiration.jpg';
import blazerImg from 'assets/landing/lookfinder/blazer.jpg';
import topImg from 'assets/landing/lookfinder/top.jpg';
import pantsImg from 'assets/landing/lookfinder/pants.jpg';
import shoesImg from 'assets/landing/lookfinder/shoes.jpg';
import './LookBuilderSection.scss';

const LookBuilderSection = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);

  const lookProducts = [
    {
      name: t('lookbuilder_product_blazer', 'Cream Blazer'),
      price: '$129.00',
      stores: 5,
      image: blazerImg,
    },
    {
      name: t('lookbuilder_product_top', 'White Tank Top'),
      price: '$25.00',
      stores: 8,
      image: topImg,
    },
    {
      name: t('lookbuilder_product_pants', 'Rose Pleated Pants'),
      price: '$89.00',
      stores: 4,
      image: pantsImg,
    },
    {
      name: t('lookbuilder_product_shoes', 'Cream Leather Loafers'),
      price: '$159.00',
      stores: 3,
      image: shoesImg,
    },
  ];

  const features = [
    {
      icon: Upload,
      title: t('lookbuilder_feature1_title', 'One-Click Upload'),
      description: t(
        'lookbuilder_feature1_desc',
        'Simply drag and drop any fashion image from Instagram, Pinterest, or your camera roll. Our AI instantly recognizes every item in seconds.',
      ),
    },
    {
      icon: Wand2,
      title: t('lookbuilder_feature2_title', 'AI-Powered Matching'),
      description: t(
        'lookbuilder_feature2_desc',
        'Advanced visual recognition finds exact matches and similar alternatives across thousands of online stores, sorted by price and availability.',
      ),
    },
    {
      icon: ShoppingBag,
      title: t('lookbuilder_feature3_title', 'Complete Look Building'),
      description: t(
        'lookbuilder_feature3_desc',
        'Build entire outfits effortlessly. Mix and match items, compare prices across retailers, and create shoppable looks in minutes.',
      ),
    },
  ];

  const steps = [
    {
      number: 1,
      icon: Upload,
      title: t('lookbuilder_step1_title', 'Upload Your Inspiration'),
      description: t(
        'lookbuilder_step1_desc',
        'Found a stunning outfit on social media? Upload the image and let our AI do the heavy lifting. Works with any fashion photo.',
      ),
    },
    {
      number: 2,
      icon: Sparkles,
      title: t('lookbuilder_step2_title', 'AI Identifies Items'),
      description: t(
        'lookbuilder_step2_desc',
        'Our intelligent system recognizes each piece—blazers, tops, pants, shoes, and accessories—with remarkable accuracy in real-time.',
      ),
    },
    {
      number: 3,
      icon: TrendingUp,
      title: t('lookbuilder_step3_title', 'Browse Smart Matches'),
      description: t(
        'lookbuilder_step3_desc',
        'View curated results from multiple stores with prices, availability, and ratings. Filter by budget, brand, or style preference instantly.',
      ),
    },
    {
      number: 4,
      icon: ShoppingBag,
      title: t('lookbuilder_step4_title', 'Build & Purchase'),
      description: t(
        'lookbuilder_step4_desc',
        'Add items to your look, save to wishlists, share with friends, or buy directly. Complete your outfit with confidence and ease.',
      ),
    },
  ];

  const benefits = [
    t('lookbuilder_benefit1', 'Find any fashion item from any photo in seconds'),
    t('lookbuilder_benefit2', 'Compare prices across 1000+ online fashion stores'),
    t('lookbuilder_benefit3', 'Build complete outfits with AI-powered suggestions'),
    t('lookbuilder_benefit4', 'Save time hunting for the perfect pieces'),
    t('lookbuilder_benefit5', 'Discover better deals automatically'),
    t('lookbuilder_benefit6', 'Create and share shoppable lookbooks'),
  ];

  const totalPrice = lookProducts.reduce((sum, product) => {
    return sum + parseFloat(product.price.replace('$', ''));
  }, 0);

  return (
    <section className='lookbuilder-section'>
      <div className='lookbuilder-section__container'>
        {/* Header */}
        <div className='lookbuilder-section__header'>
          <div className='lookbuilder-section__badge'>
            <Sparkles className='lookbuilder-section__badge-icon' aria-hidden='true' />
            <span className='lookbuilder-section__badge-text'>{t('lookbuilder_badge', 'AI-POWERED')}</span>
          </div>

          <h1 className='lookbuilder-section__title'>
            {t('lookbuilder_title_1', 'From Inspiration to')}{' '}
            <span className='lookbuilder-section__title--gradient'>{t('lookbuilder_title_2', 'Purchase')}</span>
          </h1>

          <p className='lookbuilder-section__description'>
            {t(
              'lookbuilder_description',
              'Turn any fashion inspiration into a complete shoppable outfit. Upload a photo, and our AI instantly finds every item across thousands of stores—making it effortless to recreate the looks you love.',
            )}
          </p>
        </div>

        {/* Hero Demo - Inspiration to Products */}
        <div className='lookbuilder-section__demo'>
          <Card className='lookbuilder-section__demo-card'>
            <div className='lookbuilder-section__demo-grid'>
              {/* Left: Uploaded Photo */}
              <div className='lookbuilder-section__demo-left'>
                <div className='lookbuilder-section__uploaded-badge'>
                  <Upload className='lookbuilder-section__uploaded-icon' />
                  <span>{t('lookbuilder_uploaded_photo', 'UPLOADED PHOTO')}</span>
                </div>
                <div className='lookbuilder-section__image-wrapper'>
                  <img
                    src={inspirationImg}
                    alt={t('lookbuilder_inspiration_alt', 'Fashion inspiration outfit')}
                    className='lookbuilder-section__inspiration-img'
                  />
                  <div className='lookbuilder-section__image-overlay'>
                    <Zap className='lookbuilder-section__overlay-icon' />
                    <span className='lookbuilder-section__overlay-text'>
                      {t('lookbuilder_ai_analyzing', 'AI Analyzing...')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Shop This Look */}
              <div className='lookbuilder-section__demo-right'>
                <div className='lookbuilder-section__shop-header'>
                  <ShoppingBag className='lookbuilder-section__shop-icon' />
                  <h3 className='lookbuilder-section__shop-title'>
                    {t('lookbuilder_shop_this_look', 'SHOP THIS LOOK')}
                  </h3>
                </div>

                <div className='lookbuilder-section__products'>
                  {lookProducts.map((product, index) => (
                    <div key={index} className='lookbuilder-section__product-item'>
                      <img src={product.image} alt={product.name} className='lookbuilder-section__product-img' />
                      <div className='lookbuilder-section__product-info'>
                        <h4 className='lookbuilder-section__product-name'>{product.name}</h4>
                        <p className='lookbuilder-section__product-price'>{product.price}</p>
                        <p className='lookbuilder-section__product-stores'>
                          {t('lookbuilder_available_at', 'Available at')} {product.stores}{' '}
                          {t('lookbuilder_stores', 'stores')}
                        </p>
                      </div>
                      <Button variant='outline' size='sm' className='lookbuilder-section__product-btn'>
                        {t('lookbuilder_view_product', 'View Product')}
                      </Button>
                    </div>
                  ))}
                </div>

                <div className='lookbuilder-section__total'>
                  <span className='lookbuilder-section__total-label'>{t('lookbuilder_total_look', 'Total Look:')}</span>
                  <span className='lookbuilder-section__total-price'>${totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* AI Analysis Badge */}
            <div className='lookbuilder-section__analysis'>
              <div className='lookbuilder-section__analysis-header'>
                <Zap className='lookbuilder-section__analysis-icon' />
                <h4 className='lookbuilder-section__analysis-title'>
                  {t('lookbuilder_ai_analysis', 'AI Analysis Complete')}
                </h4>
              </div>
              <ul className='lookbuilder-section__analysis-list'>
                <li>
                  <CheckCircle2 className='lookbuilder-section__check-icon' />
                  <span>{t('lookbuilder_analysis_1', '4 items identified and matched')}</span>
                </li>
                <li>
                  <CheckCircle2 className='lookbuilder-section__check-icon' />
                  <span>{t('lookbuilder_analysis_2', '20+ similar alternatives found')}</span>
                </li>
                <li>
                  <CheckCircle2 className='lookbuilder-section__check-icon' />
                  <span>{t('lookbuilder_analysis_3', 'Prices compared across 1,000+ stores')}</span>
                </li>
              </ul>
            </div>
          </Card>
        </div>

        {/* Key Features */}
        <div className='lookbuilder-section__features'>
          <div className='lookbuilder-section__features-header'>
            <h2 className='lookbuilder-section__features-title'>
              {t('lookbuilder_features_title_1', 'Effortless Fashion Discovery')}{' '}
              <span className='lookbuilder-section__title--gradient'>
                {t('lookbuilder_features_title_2', 'Made Simple')}
              </span>
            </h2>
            <p className='lookbuilder-section__features-desc'>
              {t(
                'lookbuilder_features_desc',
                'Likiee eliminates the frustration of endless searching. Our AI technology makes finding and building your perfect look as easy as uploading a photo.',
              )}
            </p>
          </div>

          <div className='lookbuilder-section__features-grid'>
            {features.map((feature, index) => (
              <Card key={index} className='lookbuilder-section__feature-card'>
                <div className='lookbuilder-section__feature-icon-wrapper'>
                  <feature.icon className='lookbuilder-section__feature-icon' />
                </div>
                <h3 className='lookbuilder-section__feature-title'>{feature.title}</h3>
                <p className='lookbuilder-section__feature-description'>{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* How It Works Steps */}
        <div className='lookbuilder-section__steps'>
          <div className='lookbuilder-section__steps-header'>
            <h2 className='lookbuilder-section__steps-title'>
              {t('lookbuilder_steps_title_1', 'Build Your Perfect Look in')}{' '}
              <span className='lookbuilder-section__title--gradient'>
                {t('lookbuilder_steps_title_2', '4 Simple Steps')}
              </span>
            </h2>
            <p className='lookbuilder-section__steps-desc'>
              {t(
                'lookbuilder_steps_desc',
                'From inspiration to your shopping cart in minutes. No more endless scrolling or manual searching—let AI do the work for you.',
              )}
            </p>
          </div>

          <div className='lookbuilder-section__steps-grid'>
            {steps.map((step, index) => (
              <Card
                key={index}
                className={`lookbuilder-section__step-card ${
                  activeStep === step.number ? 'lookbuilder-section__step-card--active' : ''
                }`}
                onClick={() => setActiveStep(step.number)}
              >
                <div className='lookbuilder-section__step-number'>{step.number}</div>
                <div className='lookbuilder-section__step-icon-wrapper'>
                  <step.icon className='lookbuilder-section__step-icon' />
                </div>
                <h3 className='lookbuilder-section__step-title'>{step.title}</h3>
                <p className='lookbuilder-section__step-description'>{step.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className='lookbuilder-section__benefits-section'>
          <div className='lookbuilder-section__benefits-grid'>
            {/* Left: Content */}
            <div className='lookbuilder-section__benefits-content'>
              <div className='lookbuilder-section__badge'>
                <Star className='lookbuilder-section__badge-icon' aria-hidden='true' />
                <span className='lookbuilder-section__badge-text'>{t('lookbuilder_why_badge', 'WHY LIKIEE')}</span>
              </div>

              <h2 className='lookbuilder-section__benefits-title'>
                {t('lookbuilder_benefits_title_1', 'Your Personal')}{' '}
                <span className='lookbuilder-section__title--gradient'>
                  {t('lookbuilder_benefits_title_2', 'Fashion Assistant')}
                </span>
              </h2>

              <p className='lookbuilder-section__benefits-intro'>
                {t(
                  'lookbuilder_benefits_intro',
                  'Stop wasting hours searching for that perfect piece you saw online. Likiee AI-powered platform transforms how you discover, save, and shop for fashion—making it faster, smarter, and more enjoyable.',
                )}
              </p>

              <div className='lookbuilder-section__benefits-list'>
                {benefits.map((benefit, index) => (
                  <div key={index} className='lookbuilder-section__benefit-item'>
                    <CheckCircle2 className='lookbuilder-section__benefit-icon' />
                    <span className='lookbuilder-section__benefit-text'>{benefit}</span>
                  </div>
                ))}
              </div>

              <div className='lookbuilder-section__cta-group'>
                <a href={URLS.CONTACT} className='lookbuilder-section__cta-link'>
                  <Button variant='default' size='lg' className='lookbuilder-section__cta-primary'>
                    <Sparkles className='lookbuilder-section__btn-icon' />
                    {t('contact_us', 'Contact us')}
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: Stats Card */}
            <div className='lookbuilder-section__benefits-visual'>
              <Card className='lookbuilder-section__stats-card'>
                <div className='lookbuilder-section__stat-item'>
                  <div className='lookbuilder-section__stat-icon-wrapper'>
                    <Upload className='lookbuilder-section__stat-icon' />
                  </div>
                  <div className='lookbuilder-section__stat-content'>
                    <p className='lookbuilder-section__stat-number'>10K+</p>
                    <p className='lookbuilder-section__stat-label'>{t('lookbuilder_stat1', 'Looks Created Daily')}</p>
                  </div>
                </div>

                <div className='lookbuilder-section__stat-item'>
                  <div className='lookbuilder-section__stat-icon-wrapper'>
                    <ShoppingBag className='lookbuilder-section__stat-icon' />
                  </div>
                  <div className='lookbuilder-section__stat-content'>
                    <p className='lookbuilder-section__stat-number'>1,000+</p>
                    <p className='lookbuilder-section__stat-label'>{t('lookbuilder_stat2', 'Partner Stores')}</p>
                  </div>
                </div>

                <div className='lookbuilder-section__stat-item'>
                  <div className='lookbuilder-section__stat-icon-wrapper'>
                    <Zap className='lookbuilder-section__stat-icon' />
                  </div>
                  <div className='lookbuilder-section__stat-content'>
                    <p className='lookbuilder-section__stat-number'>98%</p>
                    <p className='lookbuilder-section__stat-label'>{t('lookbuilder_stat3', 'Match Accuracy')}</p>
                  </div>
                </div>

                <div className='lookbuilder-section__stat-item'>
                  <div className='lookbuilder-section__stat-icon-wrapper'>
                    <Heart className='lookbuilder-section__stat-icon' />
                  </div>
                  <div className='lookbuilder-section__stat-content'>
                    <p className='lookbuilder-section__stat-number'>4.9/5</p>
                    <p className='lookbuilder-section__stat-label'>{t('lookbuilder_stat4', 'User Satisfaction')}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LookBuilderSection;
